import { useState } from 'react';
import { Button, Dialog, Text } from 'frosted-ui';
import { Trash16 } from '@frosted-ui/icons';
import { api } from '../../services/api';

interface DeleteDraftButtonProps {
  experimentId: string;
  /** Called after the draft is gone (deleted, or archived if it had activity). */
  onDiscarded: (deleted: boolean) => void;
  /** A pay-to-unlock draft has a hidden product of its own on Whop. */
  isUnlock?: boolean;
}

/**
 * "Delete draft" with a confirm step. The server deletes a draft nobody has
 * visited and archives anything else, so no results are ever lost.
 */
export default function DeleteDraftButton({ experimentId, onDiscarded, isUnlock = false }: DeleteDraftButtonProps) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      const { deleted } = await api.discardDraft(experimentId);
      setConfirming(false);
      onDiscarded(deleted);
    } catch (err) {
      console.error('Failed to delete draft:', err);
      setError('Could not delete the draft — try again.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        color="red"
        size="2"
        style={{ minHeight: 44 }}
        onClick={() => setConfirming(true)}
      >
        <Trash16 />
        Delete draft
      </Button>
      {confirming && (
        <Dialog.Root open onOpenChange={(open: boolean) => !open && !deleting && setConfirming(false)}>
          <Dialog.Content style={{ maxWidth: 420 }}>
            <Dialog.Title size="4" style={{ marginBottom: 'var(--space-2)' }}>
              Delete this draft?
            </Dialog.Title>
            <Dialog.Description size="2" color="gray" style={{ marginBottom: 'var(--space-4)' }}>
              Its prices and links go away. Your store and real prices aren't touched.
              {isUnlock &&
                ' An empty hidden product may stay in your Whop dashboard. Buyers never see it, and you can delete it there.'}
            </Dialog.Description>
            {error && (
              <Text size="2" color="red" role="alert" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
                {error}
              </Text>
            )}
            <div className="vara2-button-stack" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
              <Button
                variant="solid"
                color="red"
                size="3"
                style={{ flex: '1 1 160px' }}
                onClick={handleDelete}
                loading={deleting}
                disabled={deleting}
              >
                Delete draft
              </Button>
              <Button
                variant="soft"
                color="gray"
                size="3"
                style={{ flex: '1 1 160px' }}
                onClick={() => setConfirming(false)}
                disabled={deleting}
              >
                Keep it
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Root>
      )}
    </>
  );
}
